import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import RingCustomizerContainer from './pages';
import URLScreening from './pages/URLScreening';
import type { ICustomizerHOCProps } from './components/RingCustomizerHOC';
import { PATH_PREFIX } from './config/tenant';
import StoreNumberGate from './components/StoreNumberGate';
import ErrorBoundary from './components/ErrorBoundary';
import { useQrScanner } from './hooks/useQrScanner';
import Loading from './components/Shared/Loader';

function createHistory(navigate: ReturnType<typeof useNavigate>) {
  return {
    push: (to: string) => {
      navigate(to);
    },
    replace: (to: string) => {
      navigate(to, { replace: true });
    },
    goBack: () => {
      navigate(-1);
    }
  };
}

function RingCustomizerRoute({ type }: { type: 1 | 2 | 3 | 4 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const history = useMemo(() => createHistory(navigate), [navigate]);
  const match = {
    params,
    isExact: true,
    path: location.pathname,
    url: location.pathname
  };
  const hocProps: ICustomizerHOCProps = {
    type,
    location,
    history,
    match
  };
  return <RingCustomizerContainer {...hocProps} />;
}

function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function QrScanner() {
  const { isScanning, isNavigating } = useQrScanner();
  return (
    <Loading
      timedOut={isScanning || isNavigating}
      message={isNavigating ? 'Opening scanned link...' : 'Reading QR code...'}
    />
  );
}

function URLScreeningRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const history = useMemo(() => createHistory(navigate), [navigate]);
  const match = {
    params,
    isExact: true,
    path: '*',
    url: location.pathname
  };
  return <URLScreening match={match} location={location} history={history} />;
}

const DEFAULT_REDIRECT = `/${PATH_PREFIX}/rings/profiles/D/widths/7/ring_materials/14KW?ring_finish=SATIN&finish_type=F&weight=P&size=10&feature_group=N&sleeve=NONE`;
const SALESFORCE_REDIRECT = `/on/demandware.store/Sites-benbridge-Site/en_US/Product-ProductBuilder?ring_url=${encodeURIComponent(DEFAULT_REDIRECT)}`;

function App() {
  return (
    <div className="tw-flex tw-flex-col app-container">
      <BrowserRouter>
        <RouteErrorBoundary>
          <StoreNumberGate />
          <QrScanner />
          <Routes>
            <Route
              path="/rings/profiles/:profile/widths/:width/ring_materials/:ring_materials"
              element={<RingCustomizerRoute type={1} />}
            />
            <Route
              path="/:pathPrefix/rings/profiles/:profile/widths/:width/ring_materials/:ring_materials"
              element={<RingCustomizerRoute type={1} />}
            />
            <Route
              path="/women/rings/styles/:styles/ring_materials/:ring_materials/color_arrangements/:color_arrangements/stone_quantities/:stone_quantities/variants/:variants"
              element={<RingCustomizerRoute type={2} />}
            />
            <Route
              path="/:pathPrefix/women/rings/styles/:styles/ring_materials/:ring_materials/color_arrangements/:color_arrangements/stone_quantities/:stone_quantities/variants/:variants"
              element={<RingCustomizerRoute type={2} />}
            />
            <Route path="/rings/:ring_type/ring_materials/:ring_materials" element={<RingCustomizerRoute type={3} />} />
            <Route
              path="/:pathPrefix/rings/:ring_type/ring_materials/:ring_materials"
              element={<RingCustomizerRoute type={3} />}
            />

            <Route
              path="/rings/:ring_type/profiles/:profile/widths/:width/ring_materials/:ring_materials"
              element={<RingCustomizerRoute type={4} />}
            />
            <Route
              path="/:pathPrefix/rings/:ring_type/profiles/:profile/widths/:width/ring_materials/:ring_materials"
              element={<RingCustomizerRoute type={4} />}
            />
            <Route path="/query" element={<Navigate to={SALESFORCE_REDIRECT} replace />} />
            <Route path="/" element={<Navigate to={DEFAULT_REDIRECT} replace />} />
            <Route path="*" element={<URLScreeningRoute />} />
          </Routes>
        </RouteErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
